import React from 'react'
import { Row, Col, Button, Card, CardHeader, CardTitle, CardBody } from 'reactstrap'
import ReactTable from '../../components/Lists/ReactTableWrapper'
import { Alert, Box, Chip, Stack, Typography } from '@mui/material'
import { withFirebase } from '../../components/Fb'
import FalconModal from '../../components/Inputs/FalconModal'
import ContractTemplateDeleteDialog from '../../components/Dialogs/ContractTemplateDeleteDialog'
import { getTeamFormatSchema, getTeamFormatUiSchema } from '../../components/Inputs/dataschemas'
import {
  describeFormat,
  formatMinutes,
  spanTable,
  totalCourtTime,
  validRows,
} from '../../components/utils/teamformats'

// Ottelumuodot. docs/features/teams.md §4.2 and Phase B.
//
// A format is the menu a manager books from: which rubbers a tie may contain and how many of
// each. It carries no layout - no offsets, no ordering, no parallel/sequential flag - because
// arrangement is the home team's call and nothing in the feature checks it.
//
// The readout in the editor's footer is the point of this screen. A venue types a format in the
// abstract; the readout turns it into "this needs three hours of one court, or 90 minutes of
// four" while they are still typing, which is when a wrong format is cheap to fix. It lives in
// the modal footer via renderFooter rather than under the table, where the open modal would
// cover it.

// The span readout is a planning aid, not a constraint (§4.2). Say so, or a venue will read it
// as a promise about what teams will actually book.
const SpanReadout = ({ format }) => {
  const table = spanTable(format)
  const total = totalCourtTime(format)
  if (!table.length) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Lisää otteluita nähdäksesi paljonko kenttäaikaa ottelumuoto vie.
      </Typography>
    )
  }
  return (
    <Box>
      <Typography variant='body2' gutterBottom>
        <b>Kenttäaikaa yhteensä:</b> {formatMinutes(total)} per kohtaaminen
      </Typography>
      <Typography variant='body2' gutterBottom>
        <b>Kohtaamisen kesto kenttämäärän mukaan:</b>
      </Typography>
      <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {table.map((r) => (
          <Chip
            key={r.courts}
            size='small'
            label={r.courts + (r.courts === 1 ? ' kenttä: ' : ' kenttää: ') + formatMinutes(r.span)}
          />
        ))}
      </Stack>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
        Suuntaa-antava: kestot lasketaan sijoittamalla pisin ottelu aina ensimmäiselle vapautuvalle
        kentälle. Joukkueenjohtaja päättää itse pelijärjestyksen, eikä mikään pakota tähän kestoon.
      </Typography>
    </Box>
  )
}

class TeamFormatsContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      formats: [],
      selectedFormat: null,
      modalOpen: false,
      confirmDelete: null,
      deleting: false,
      loaderror: null,
    }
  }

  componentDidMount() {
    this.loadFormats()
  }

  loadFormats = async () => {
    try {
      const snap = await this.props.firebase.getTeamFormats()
      // Filtered client side, NOT with .where('state','!=','deleted'): a != query drops every
      // document that has no state field at all. Same reasoning as getRecurringInquiries.
      const formats = snap.empty
        ? []
        : snap.docs
            .map((d) => ({ ...d.data(), id: d.id }))
            .filter((f) => f.state !== 'deleted')
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      this.setState({ formats, loading: false, loaderror: null })
    } catch (error) {
      console.error('Loading team formats failed', error)
      this.setState({ loading: false, loaderror: error.message })
    }
  }

  // The grid's rows are derived copies, not the format documents. FalconModal writes back the
  // object it was opened with, so a summary hung on a format would be saved to Firestore as a
  // field nothing reads.
  tableRows = () =>
    this.state.formats.map((f) => ({
      id: f.id,
      name: f.name,
      rubberssummary: describeFormat(f),
      courttime: totalCourtTime(f),
      activetext: f.active ? 'Kyllä' : 'Ei',
    }))

  toggleModal = () => {
    this.setState({ modalOpen: !this.state.modalOpen })
  }

  addNewFormat = () => {
    this.setState({ selectedFormat: { active: true, rubbers: [] } }, this.toggleModal)
  }

  handleRowSelect = (id) => {
    const format = this.state.formats.find((f) => f.id === id)
    if (format) this.setState({ selectedFormat: format }, this.toggleModal)
  }

  onSaved = async () => {
    await this.loadFormats()
  }

  askDelete = (event, format) => {
    event.stopPropagation()
    this.setState({ confirmDelete: format })
  }

  cancelDelete = () => {
    this.setState({ confirmDelete: null })
  }

  confirmDelete = async () => {
    const format = this.state.confirmDelete
    if (!format) return
    this.setState({ deleting: true })
    try {
      await this.props.firebase.deleteTeamFormat(format.id)
      await this.loadFormats()
      this.setState({ confirmDelete: null, deleting: false })
    } catch (error) {
      console.error('Deleting team format failed', error)
      this.setState({ deleting: false })
    }
  }

  createModal = () => {
    return (
      <FalconModal
        onCreate={this.props.firebase.createTeamFormat}
        onUpdate={this.props.firebase.updateTeamFormat}
        modifiedCallback={this.onSaved}
        toggle={this.toggleModal}
        cancel={this.toggleModal}
        resource={this.state.selectedFormat}
        isOpen={this.state.modalOpen}
        uiSchema={getTeamFormatUiSchema()}
        dataSchema={getTeamFormatSchema()}
        disableSave={(formData) => !validRows(formData).length}
        renderFooter={(formData) => <SpanReadout format={formData} />}
        size='lg'
        title={
          this.state.selectedFormat && this.state.selectedFormat.id ? 'Muokkaa ottelumuotoa' : 'Uusi ottelumuoto'
        }
      />
    )
  }

  render() {
    if (this.state.loading) {
      return <div className='content'>Loading ...</div>
    }
    return (
      <>
        {/* .content is what supplies the 70px top margin that clears the fixed navbar
            (_sidebar-and-main-panel.scss:522). Without it the card slides under the topbar and
            the card title collides with the breadcrumb. */}
        <div className='content'>
          <Card>
            <CardHeader>
              <Row>
                <Col lg='10' md='5' sm='5'>
                  <CardTitle tag='h5'>Ottelumuodot</CardTitle>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {this.state.loaderror ? (
                <Row>
                  <Col>
                    <Alert severity='error' sx={{ mb: 2 }}>
                      Ottelumuotojen lataus epäonnistui: {this.state.loaderror}
                    </Alert>
                  </Col>
                </Row>
              ) : null}
              <Row>
                <Col>
                  <Button onClick={this.addNewFormat}>Lisää ottelumuoto</Button>
                </Col>
              </Row>
              <Row>
                <Col md='12'>
                  <ReactTable
                    data={this.tableRows()}
                    noSumRow={true}
                    columns={[
                      { Header: 'Nimi', accessor: 'name' },
                      { Header: 'Ottelut', accessor: 'rubberssummary', sortable: false },
                      {
                        Header: 'Kenttäaikaa',
                        accessor: 'courttime',
                        width: 130,
                        valueFormatter: (value) => formatMinutes(value),
                      },
                      { Header: 'Käytössä', accessor: 'activetext', width: 110 },
                      {
                        // accessor 'actions' is what makes the wrapper set
                        // disableSelectionOnClick, so the delete button does not also select.
                        Header: '',
                        accessor: 'actions',
                        sortable: false,
                        filterable: false,
                        width: 90,
                        renderCell: (params) => (
                          <Button size='sm' color='link' onClick={(e) => this.askDelete(e, params.row)}>
                            Poista
                          </Button>
                        ),
                      },
                    ]}
                    onRowClick={(params) => this.handleRowSelect(params.row.id)}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
          {this.createModal()}
          <ContractTemplateDeleteDialog
            open={!!this.state.confirmDelete}
            name={this.state.confirmDelete ? this.state.confirmDelete.name : ''}
            working={this.state.deleting}
            onConfirm={this.confirmDelete}
            onCancel={this.cancelDelete}
          />
        </div>
      </>
    )
  }
}

export default withFirebase(TeamFormatsContainer)
